import { Button, Flex, FlexProps, Paper, Text } from '@mantine/core';
import { Textarea } from 'react-hook-form-mantine';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { DataBankAccountTct } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { BankAccountTile } from '@dealstack/frontend/shared/components/BankAccountTile/BankAccountTile.tsx';
import { BankAccountFormDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountFormDrawer.tsx';
import { BankAccountDeleteDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountDeleteDrawer.tsx';
import { useProgrammeBankAccount } from './useProgrammeBankAccount.ts';
import { useBankAccountLifecycleDebug } from '@dealstack/frontend/app/components/BankAccountLifecycleDebug/useBankAccountLifecycleDebug.tsx';

interface Props {
	bankAccount?: DataBankAccountTct;
	instructionsFieldName: string;
	width?: FlexProps['w'];
	gap?: FlexProps['gap'];
}

export const ProgrammeBankAccountSection = ({
	bankAccount,
	instructionsFieldName,
	width,
	gap = '0.5rem',
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();

	const {
		hasEditPermission,
		verifyIdentifiers,
		isDrawerOpen,
		openDrawer,
		closeDrawer,
		isDeleteOpen,
		openDelete,
		closeDelete,
		onFormSubmit,
		isRequestLoading,
		deleteBankAccount,
		isDeleteLoading,
		isDeleteSuccess,
	} = useProgrammeBankAccount(bankAccount);
	const { onInspect, drawer: lifecycleDebugDrawer } = useBankAccountLifecycleDebug();

	return (
		<Flex direction="column" gap={gap} w={width}>
			<Flex direction="column" gap="0.5rem">
				<Text fw={600}>{tMipManage('settings_modal.bank_details')}</Text>

				{bankAccount ? (
					<BankAccountTile
						bankAccount={bankAccount}
						onEdit={openDrawer}
						onRemove={openDelete}
						hasEditPermission={hasEditPermission}
						onInspect={onInspect}
						showStatusBadge
						tileTestId={TEST_ELEMENT_IDS.PROGRAMME_BANK_ACCOUNT_TILE}
						editBtnTestId={TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON}
						deleteBtnTestId={TEST_ELEMENT_IDS.EDITABLE_TILE_DELETE_BUTTON}
						statusBadgeTestId={TEST_ELEMENT_IDS.CAP_BANK_ACCOUNT_TILE}
						withBorder
					/>
				) : (
					<Paper withBorder p="1rem">
						<Flex align="center" justify="space-between" gap="0.5rem">
							<Flex direction="column" gap=".25rem">
								<Text fw={600}>
									{tMipManage('settings_modal.bank_account.empty_title')}
								</Text>
								<Text c="dark.0">
									{tMipManage('settings_modal.bank_account.empty_description')}
								</Text>
							</Flex>
							{hasEditPermission && (
								<Button
									variant="outline"
									onClick={openDrawer}
									data-testid={TEST_ELEMENT_IDS.PROGRAMME_BANK_ACCOUNT_ADD_BUTTON}
								>
									{tMipManage('settings_modal.bank_account.add_button')}
								</Button>
							)}
						</Flex>
					</Paper>
				)}
			</Flex>

			<Textarea
				label={
					<Flex gap="0.25rem" align="center">
						<Flex>{tCommonUi('bank_account_input.instructions_label')}</Flex>
						<Tooltip tooltip={tCommonUi('bank_account_input.instructions_tooltip')} />
					</Flex>
				}
				name={instructionsFieldName}
				data-testid={TEST_ELEMENT_IDS.PROGRAMME_BANK_ACCOUNT_INSTRUCTIONS_INPUT}
				disabled={!bankAccount}
				autosize
				minRows={2}
				maxRows={16}
				size="md"
			/>

			{hasEditPermission && (
				<>
					<BankAccountFormDrawer
						opened={isDrawerOpen}
						onClose={closeDrawer}
						title={
							bankAccount
								? tMipManage('settings_modal.bank_account.edit_title')
								: tMipManage('settings_modal.bank_account.add_title')
						}
						headingTestId={TEST_ELEMENT_IDS.PROGRAMME_BANK_ACCOUNT_DRAWER_HEADING}
						hasPermission={hasEditPermission}
						bankAccount={bankAccount ?? null}
						isRequestLoading={isRequestLoading}
						onSubmit={onFormSubmit}
						verifyIdentifiers={verifyIdentifiers}
					/>

					<BankAccountDeleteDrawer
						opened={isDeleteOpen}
						onClose={closeDelete}
						bankAccount={bankAccount ?? null}
						handleDelete={() => bankAccount && deleteBankAccount(bankAccount.id)}
						deleteLoading={isDeleteLoading}
						deleteSuccess={isDeleteSuccess}
					/>
				</>
			)}
			{lifecycleDebugDrawer}
		</Flex>
	);
};
